// src/components/whatsapp/WhatsAppTemplatesPage.tsx
// Track E5 — WhatsApp templates (backed by mcp_django B2).
//
// List with status filter, create drawer, per-row submit + sync + delete.
// Edit is intentionally locked once a template leaves draft (Meta does
// not allow in-place edits of submitted copy — backend enforces this,
// the UI just stops offering the button).

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, CheckCircle, ChevronDown, ChevronRight, Clock,
  FileCheck, Loader2, MessageSquare, Plus, RefreshCw, Send,
  Trash2, XCircle,
} from 'lucide-react';

import {
  ApiError,
  createTemplate, deleteTemplate, listTemplates, submitTemplate, syncTemplate,
  type TemplateCategory, type TemplateIn, type TemplateStatus,
  type WhatsAppTemplate,
} from '@/lib/api_whatsapp';

type Notifier = (n: {
  type: 'success' | 'error' | 'info';
  title: string; message?: string; duration?: number;
}) => void;

interface Props {
  addNotification?: Notifier;
  /** Dentro de la app WhatsApp: la app pinta el título; aquí solo las acciones. */
  embedded?: boolean;
}

const STATUS_FILTERS: Array<{ id: 'all' | TemplateStatus; label: string }> = [
  { id: 'all', label: 'Todas' },
  { id: 'draft', label: 'Borrador' },
  { id: 'pending', label: 'Pendiente' },
  { id: 'approved', label: 'Aprobada' },
  { id: 'rejected', label: 'Rechazada' },
  { id: 'paused', label: 'Pausada' },
  { id: 'disabled', label: 'Desactivada' },
];

function StatusBadge({ status }: { status: TemplateStatus }) {
  const map: Record<TemplateStatus, { icon: JSX.Element; cls: string; label: string }> = {
    draft: { icon: <AlertCircle size={12} />, cls: 'bg-muted text-muted-foreground', label: 'Borrador' },
    pending: { icon: <Clock size={12} />, cls: 'bg-amber-500/15 text-amber-800 dark:text-amber-300', label: 'Pendiente' },
    approved: { icon: <CheckCircle size={12} />, cls: 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300', label: 'Aprobada' },
    rejected: { icon: <XCircle size={12} />, cls: 'bg-red-500/15 text-red-800 dark:text-red-300', label: 'Rechazada' },
    paused: { icon: <Clock size={12} />, cls: 'bg-yellow-500/15 text-yellow-800 dark:text-yellow-200', label: 'Pausada' },
    disabled: { icon: <XCircle size={12} />, cls: 'bg-muted text-muted-foreground', label: 'Desactivada' },
  };
  const s = map[status] || map.draft;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
      {s.icon} {s.label}
    </span>
  );
}

export function WhatsAppTemplatesPage({ addNotification, embedded = false }: Props) {
  const [rows, setRows] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | TemplateStatus>('all');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [pending, setPending] = useState<Record<number, 'submit' | 'sync' | 'delete'>>({});
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await listTemplates({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        limit: 100,
      });
      setRows(out.items);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : e?.message || 'Error';
      addNotification?.({ type: 'error', title: 'No se pudieron cargar las plantillas', message: msg });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, addNotification]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const acc: Record<string, number> = { all: rows.length };
    for (const r of rows) acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, [rows]);

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSubmit = async (tpl: WhatsAppTemplate) => {
    setPending((p) => ({ ...p, [tpl.id]: 'submit' }));
    try {
      const out = await submitTemplate(tpl.id);
      addNotification?.({
        type: 'success',
        title: out.status === 'queued' ? 'Enviada a Meta' : out.status,
        message: out.detail,
      });
      await load();
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : e?.message || 'Error';
      addNotification?.({ type: 'error', title: 'No se pudo enviar', message: msg });
    } finally {
      setPending((p) => { const n = { ...p }; delete n[tpl.id]; return n; });
    }
  };

  const handleSync = async (tpl: WhatsAppTemplate) => {
    setPending((p) => ({ ...p, [tpl.id]: 'sync' }));
    try {
      const out = await syncTemplate(tpl.id);
      addNotification?.({
        type: out.status === 'error' ? 'error' : 'info',
        title: 'Estado sincronizado',
        message: out.detail,
      });
      await load();
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : e?.message || 'Error';
      addNotification?.({ type: 'error', title: 'No se pudo sincronizar', message: msg });
    } finally {
      setPending((p) => { const n = { ...p }; delete n[tpl.id]; return n; });
    }
  };

  const handleDelete = async (tpl: WhatsAppTemplate) => {
    if (!window.confirm(`¿Eliminar la plantilla "${tpl.name}"?`)) return;
    setPending((p) => ({ ...p, [tpl.id]: 'delete' }));
    try {
      await deleteTemplate(tpl.id);
      addNotification?.({ type: 'success', title: 'Plantilla eliminada' });
      await load();
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : e?.message || 'Error';
      addNotification?.({ type: 'error', title: 'No se pudo eliminar', message: msg });
    } finally {
      setPending((p) => { const n = { ...p }; delete n[tpl.id]; return n; });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`flex flex-wrap items-center gap-3 ${embedded ? 'justify-end' : 'justify-between'}`}>
        {!embedded && (
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <MessageSquare className="wa-accent-text" />
            Plantillas de WhatsApp
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            Escribe la plantilla, mándala a revisión de Meta y, ya aprobada, úsala en chats y envíos masivos.
          </p>
        </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 px-3 py-2 text-sm flex items-center gap-2"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
          <button onClick={() => setShowCreate(true)} className="wa-btn inline-flex items-center justify-center rounded-lg font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] focus-visible:ring-offset-2 focus-visible:ring-offset-background px-3 py-2 text-sm flex items-center gap-2">
            <Plus size={16} /> Nueva plantilla
          </button>
        </div>
      </div>

      {/* Status filter */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setStatusFilter(f.id)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              statusFilter === f.id
                ? 'bg-muted border-border text-foreground'
                : 'bg-transparent border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/60'
            }`}
          >
            {f.label}
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-background/60">
              {counts[f.id] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {/* List */}
      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={28} className="animate-spin wa-accent-text" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <MessageSquare size={40} className="mx-auto mb-3 opacity-50" />
          <p>No hay plantillas {statusFilter !== 'all' ? 'con este filtro' : 'aún'}.</p>
          {statusFilter === 'all' && (
            <p className="text-sm mt-1">Crea tu primera plantilla para enviar mensajes proactivos.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((tpl) => {
            const busy = pending[tpl.id];
            return (
              <div key={tpl.id} className="bg-card border border-border rounded-lg overflow-hidden">
                <div
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
                  onClick={() => toggle(tpl.id)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <MessageSquare size={20} className="wa-accent-text shrink-0" />
                    <div className="min-w-0">
                      <h4 className="font-medium text-foreground truncate">{tpl.name}</h4>
                      <div className="flex items-center gap-2 mt-1 text-xs">
                        <StatusBadge status={tpl.status} />
                        <span className="text-muted-foreground">
                          {tpl.language.toUpperCase()} · {tpl.category}
                        </span>
                        {tpl.meta_template_id && (
                          <span className="text-muted-foreground truncate">id: {tpl.meta_template_id}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {expanded.has(tpl.id) ? <ChevronDown size={20} className="text-muted-foreground" /> : <ChevronRight size={20} className="text-muted-foreground" />}
                </div>

                {expanded.has(tpl.id) && (
                  <div className="p-4 border-t border-border bg-muted/30 space-y-3">
                    {tpl.header_format && (
                      <div>
                        <div className="text-xs text-muted-foreground uppercase">Encabezado ({tpl.header_format})</div>
                        <div className="text-sm text-foreground">{tpl.header_text || '—'}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-xs text-muted-foreground uppercase">Cuerpo</div>
                      <pre className="bg-background p-3 rounded-lg text-sm text-foreground whitespace-pre-wrap font-mono">
                        {tpl.body}
                      </pre>
                    </div>
                    {tpl.footer && (
                      <div>
                        <div className="text-xs text-muted-foreground uppercase">Pie</div>
                        <div className="text-sm text-foreground">{tpl.footer}</div>
                      </div>
                    )}
                    {tpl.variables_example.length > 0 && (
                      <div>
                        <div className="text-xs text-muted-foreground uppercase">Ejemplos de variables</div>
                        <div className="text-sm text-foreground">{tpl.variables_example.join(' · ')}</div>
                      </div>
                    )}
                    {tpl.rejection_reason && (
                      <div className="text-sm text-destructive">
                        <span className="uppercase text-xs">Motivo de rechazo: </span>{tpl.rejection_reason}
                      </div>
                    )}
                    {tpl.error_message && (
                      <div className="text-sm text-amber-800 dark:text-amber-300">
                        <span className="uppercase text-xs">Último error: </span>{tpl.error_message}
                      </div>
                    )}

                    <div className="flex items-center gap-2 pt-3 border-t border-border">
                      {tpl.status === 'draft' && (
                        <button
                          onClick={() => handleSubmit(tpl)}
                          disabled={!!busy}
                          className="wa-btn inline-flex items-center justify-center rounded-lg font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] focus-visible:ring-offset-2 focus-visible:ring-offset-background px-2.5 py-1.5 text-xs flex items-center gap-1"
                        >
                          {busy === 'submit' ? <Loader2 size={14} className="animate-spin" /> : <FileCheck size={14} />}
                          Enviar a revisión
                        </button>
                      )}
                      {tpl.meta_template_id && (
                        <button
                          onClick={() => handleSync(tpl)}
                          disabled={!!busy}
                          className="inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 px-2.5 py-1.5 text-xs flex items-center gap-1"
                        >
                          {busy === 'sync' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                          Sincronizar estado
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(tpl)}
                        disabled={!!busy}
                        className="inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 px-2.5 py-1.5 text-xs text-destructive flex items-center gap-1 ml-auto"
                      >
                        {busy === 'delete' ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        Eliminar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateTemplateDrawer
          onClose={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await load();
          }}
          notify={addNotification}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Create drawer
// ═══════════════════════════════════════════════════════════════

function CreateTemplateDrawer({
  onClose, onCreated, notify,
}: {
  onClose: () => void;
  onCreated: () => void | Promise<void>;
  notify?: Notifier;
}) {
  const [form, setForm] = useState<TemplateIn>({
    name: '',
    body: '',
    category: 'UTILITY',
    language: 'es_MX',
    header_format: '',
    header_text: '',
    footer: '',
    buttons: [],
    variables_example: [],
  });
  const [submitAfterCreate, setSubmitAfterCreate] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const created = await createTemplate({
        ...form,
        name: form.name.trim(),
        body: form.body.trim(),
      });
      notify?.({ type: 'success', title: 'Plantilla creada' });
      if (submitAfterCreate) {
        try {
          await submitTemplate(created.id);
          notify?.({ type: 'success', title: 'Enviada a revisión' });
        } catch (e: any) {
          notify?.({
            type: 'error',
            title: 'Creada pero no enviada',
            message: e instanceof ApiError ? e.message : e?.message || 'Error',
          });
        }
      }
      await onCreated();
    } catch (e: any) {
      notify?.({
        type: 'error',
        title: 'No se pudo crear',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card text-card-foreground rounded-xl border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-4">Nueva plantilla</h3>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Nombre (slug — lowercase, números y _)
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                  }))
                }
                placeholder="order_confirmation"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:border-emerald-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Cuerpo</label>
              <textarea
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                placeholder="Hola {{1}}, tu orden {{2}} está confirmada."
                rows={5}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:border-emerald-500 focus:outline-none font-mono text-sm"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Variables posicionales: usa {'{{1}}'}, {'{{2}}'}, etc.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Idioma</label>
                <select
                  value={form.language}
                  onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:border-emerald-500 focus:outline-none"
                >
                  <option value="es_MX">Español (MX)</option>
                  <option value="es_ES">Español (ES)</option>
                  <option value="en_US">English (US)</option>
                  <option value="pt_BR">Português (BR)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Categoría</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as TemplateCategory }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:border-emerald-500 focus:outline-none"
                >
                  <option value="UTILITY">Utilidad</option>
                  <option value="MARKETING">Marketing</option>
                  <option value="AUTHENTICATION">Autenticación</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Pie (opcional, máx 60 caracteres)
              </label>
              <input
                type="text"
                value={form.footer || ''}
                onChange={(e) => setForm((f) => ({ ...f, footer: e.target.value.slice(0, 60) }))}
                placeholder="Equipo Calaita"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:border-emerald-500 focus:outline-none"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="submit_after"
                checked={submitAfterCreate}
                onChange={(e) => setSubmitAfterCreate(e.target.checked)}
                className="w-4 h-4 rounded border-border bg-background wa-accent-text focus:ring-emerald-500"
              />
              <label htmlFor="submit_after" className="text-sm text-foreground">
                Enviar a Meta inmediatamente después de crear
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-border">
              <button type="button" onClick={onClose} className="inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 px-3 py-2 text-sm">
                Cancelar
              </button>
              <button type="submit" disabled={saving} className="wa-btn inline-flex items-center justify-center rounded-lg font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] focus-visible:ring-offset-2 focus-visible:ring-offset-background px-3 py-2 text-sm flex items-center gap-2">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Guardar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default WhatsAppTemplatesPage;
